// Typed wrapper around GET /admin/officer/:officerId/queue?expand=true.
//
// Response shape mirrors apps/projector/src/server.ts §3a §3b.
// `pending_expanded` is present iff `expand=true`; each entry's body
// is the parsed YAML of the pending review file.

import { useEffect, useState } from 'react';
import { api } from '@rmint/shell';

export type ExpandedReview = {
  path: string;
  body: unknown;
};

export type OfficerQueue = {
  ok: true;
  officerId: string;
  today: string;
  pending: string[];
  pending_expanded?: ExpandedReview[];
  in_progress: string[];
  completed_today: string[];
  invariant_violations: string[];
  pagination: {
    limit: number;
    after: string | null;
    nextCursor: string | null;
    hasMore: boolean;
  };
};

export async function fetchQueue(
  officerId: string,
  options: { expand?: boolean; today?: string } = {},
): Promise<OfficerQueue> {
  const params = new URLSearchParams();
  if (options.expand) params.set('expand', 'true');
  if (options.today) params.set('today', options.today);
  const qs = params.toString();
  const path = `/admin/officer/${encodeURIComponent(officerId)}/queue${qs ? `?${qs}` : ''}`;
  const res = await api.get(path, { responseType: 'json' });
  // The projector stamps ok:true on success; bubble error responses
  // (4xx) as exceptions so callers don't have to type-check on every
  // call site. The api instance's validateStatus<500 lets these
  // through without throwing on the network level.
  if (res.status >= 400) {
    throw new Error(`queue fetch failed: ${res.status} ${JSON.stringify(res.data)}`);
  }
  return res.data as OfficerQueue;
}

// Convenience hook used by cells. Re-fetches whenever `officerId` or
// `tick` change — `tick` is the manual-refresh handle (incremented
// by Approve/Reject in §5.7 to refresh the queue post-decision).
export function useOfficerQueue(
  officerId: string,
  options: { expand?: boolean; tick?: number } = {},
): { data: OfficerQueue | null; loading: boolean; error: string | null; reload: () => void } {
  const [data, setData] = useState<OfficerQueue | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [internalTick, setInternalTick] = useState(0);

  useEffect(() => {
    let cancelled = false;
    setLoading(true);
    setError(null);
    fetchQueue(officerId, { expand: options.expand })
      .then((q) => {
        if (!cancelled) setData(q);
      })
      .catch((e) => {
        if (!cancelled) setError(e instanceof Error ? e.message : String(e));
      })
      .finally(() => {
        if (!cancelled) setLoading(false);
      });
    return () => {
      cancelled = true;
    };
  }, [officerId, options.expand, options.tick, internalTick]);

  return { data, loading, error, reload: () => setInternalTick((t) => t + 1) };
}
